/**
 * PermissionsModal Component
 *
 * Standalone modal for viewing application permissions and associated roles.
 */

import React from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Alert } from '../../../components/ui/Alert';
import { Spinner } from '../../../components/ui/Spinner';
import { useRolesByApplication } from '../../roles/hooks/useRoles';
import type { ApplicationSanitized } from '../../../types/application.types';
import type { PermissionValue } from '../../../types/role.types';

interface PermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  application: ApplicationSanitized;
  canManagePermissions?: boolean;
}

/**
 * Format permission for display
 */
const formatPermission = (permission: PermissionValue): string => {
  if (typeof permission === 'string') {
    return permission;
  }
  return `${permission.resource}: [${permission.actions.join(', ')}]`;
};

/**
 * PermissionsModal component
 */
export const PermissionsModal: React.FC<PermissionsModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  application,
  canManagePermissions = false,
}) => {
  // Fetch roles that have permissions for this application
  const { data: roles = [], isLoading, error } = useRolesByApplication(tenantId, application.applicationId);

  const handleManageRoles = () => {
    window.location.href = `/app/roles?application=${application.applicationId}`;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Header>
        <div className="flex flex-col">
          <span>Permissions - {application.applicationName}</span>
          <span className="text-sm font-normal text-gray-500 font-mono">
            {application.applicationCode}
          </span>
        </div>
      </Modal.Header>

      <Modal.Body>
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <Alert variant="error">
            Failed to load permissions. Please try again.
          </Alert>
        )}

        {/* Content */}
        {!isLoading && !error && (
          <div className="space-y-4">
            {/* Header with Manage Button */}
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-semibold text-gray-900">
                Roles ({roles.length})
              </h4>
              {canManagePermissions && (
                <Button size="sm" variant="secondary" onClick={handleManageRoles}>
                  Manage Roles
                </Button>
              )}
            </div>

            {/* Roles List */}
            {roles.length > 0 ? (
              <div className="space-y-3">
                {roles.map((role) => {
                  const assignmentCount =
                    (role.users?.length || 0) +
                    (role.groups?.length || 0);

                  return (
                    <div
                      key={role.roleId}
                      className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h5 className="text-sm font-semibold text-gray-900">
                            {role.roleName}
                          </h5>
                          {role.description && (
                            <p className="text-xs text-gray-600 mt-1">{role.description}</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Badge variant="info">
                            {role.permissions.length} permission{role.permissions.length !== 1 ? 's' : ''}
                          </Badge>
                          {assignmentCount > 0 && (
                            <Badge variant="success">
                              {assignmentCount} assignment{assignmentCount !== 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Permissions Preview */}
                      <div className="mt-3">
                        <p className="text-xs font-medium text-gray-500 mb-2">Permissions:</p>
                        <div className="flex flex-wrap gap-1">
                          {role.permissions.slice(0, 5).map((permission, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-1 rounded text-xs font-mono bg-gray-100 text-gray-700"
                            >
                              {formatPermission(permission)}
                            </span>
                          ))}
                          {role.permissions.length > 5 && (
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs text-gray-500">
                              +{role.permissions.length - 5} more
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Alert variant="info">
                <div className="space-y-2">
                  <p className="font-semibold">No roles configured yet</p>
                  <p className="text-sm">
                    Create roles to define permissions for this application. Roles can be assigned
                    to users and groups to control access.
                  </p>
                  {canManagePermissions && (
                    <Button size="sm" variant="primary" onClick={handleManageRoles}>
                      Create First Role
                    </Button>
                  )}
                </div>
              </Alert>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
